import type { InputHTMLAttributes, Ref, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../cn.ts';
import { useFieldControl } from './FormField.tsx';

// Shared look of text controls, from the fork's components-next/input/Input.vue.
export const controlClass =
  'block w-full text-sm text-n-slate-12 rounded-lg border-0 outline outline-1 -outline-offset-1 bg-n-alpha-black2 ' +
  'placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 transition-[outline-color] duration-200';
const outlineOk = 'outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand';
const outlineError = 'outline-n-ruby-8 hover:outline-n-ruby-9 focus:outline-n-ruby-9';

export type ControlSize = 'sm' | 'md';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: ControlSize;
  ref?: Ref<HTMLInputElement>;
}

export function Input({ size = 'md', className, ref, ...props }: InputProps) {
  const { invalid, ...field } = useFieldControl(props);
  return (
    <input
      ref={ref}
      {...props}
      {...field}
      className={cn(
        controlClass,
        invalid ? outlineError : outlineOk,
        size === 'sm' ? 'h-8 px-3 py-2' : 'h-10 px-3 py-2.5',
        className,
      )}
    />
  );
}

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  ref?: Ref<HTMLTextAreaElement>;
}

export function Textarea({ className, rows = 4, ref, ...props }: TextareaProps) {
  const { invalid, ...field } = useFieldControl(props);
  return (
    <textarea
      ref={ref}
      rows={rows}
      {...props}
      {...field}
      className={cn(
        controlClass,
        invalid ? outlineError : outlineOk,
        'px-3 py-2.5 min-h-20 resize-y',
        className,
      )}
    />
  );
}

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  size?: ControlSize;
  /** Rendered before `children`. */
  options?: SelectOption[];
  /** Adds a first, empty option. */
  placeholder?: string;
  ref?: Ref<HTMLSelectElement>;
}

/** A native select styled like the dashboard inputs (native keeps keyboard and mobile behaviour). */
export function Select({
  size = 'md',
  options,
  placeholder,
  className,
  children,
  ref,
  ...props
}: SelectProps) {
  const { invalid, ...field } = useFieldControl(props);
  return (
    <div className={cn('relative', className)}>
      <select
        ref={ref}
        {...props}
        {...field}
        className={cn(
          controlClass,
          invalid ? outlineError : outlineOk,
          'appearance-none pe-9 cursor-pointer',
          size === 'sm' ? 'h-8 ps-3' : 'h-10 ps-3',
        )}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options?.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
        {children}
      </select>
      <ChevronDown
        aria-hidden="true"
        className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 size-4 text-n-slate-11"
      />
    </div>
  );
}
